import { Modal, FloatingLabel, Button, Form, Container, Row, Col, Badge, InputGroup } from "react-bootstrap"
import { InputSetup, InputTypes, IntegrationSystem } from "../../models/Models"
import { AuthContext } from "../../models/AuthContextProvider";
import { useContext, useEffect, useState } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { title } from "process";
import cronstrue from 'cronstrue';
import 'cronstrue/locales/ru';

type JobParam = {
    name: string;
    value: string;
};

type JobSettings = {
    id: string;
    cronExpression: string;
    timeZoneId: string;
    params: JobParam[];
};

// Тип для данных о временной зоне с бекенда
type TimeZoneViewModel = {
    id: string;
    displayName: string;
};

export function InputInterfaceEditor(props: {
    editor: InputSetup
    setEditor: React.Dispatch<React.SetStateAction<InputSetup | undefined>>
    reload: () => void
}) {
    let context = useContext(AuthContext);
    const [integrationSystems, setIntegrationSystems] = useState<IntegrationSystem[]>(() => context.environment.integrationSystems.filter(s => props.editor.SystemIds.includes(s.Id)));
    const [cron, setCron] = useState<JobSettings[]>([]);
    const [availableTimeZones, setAvailableTimeZones] = useState<TimeZoneViewModel[]>([]); // Состояние для списка временных зон
    const editorId = props.editor.Id;

    useEffect(() => {
        (async () => {
            try {
                const [tzResponse, jobsResponse] = await Promise.all([
                    fetch('/Integrations/GetAvailableTimeZones'),
                    fetch(`/Integrations/GetRecurringJobs/${editorId}`)
                ]);
                let tzs: TimeZoneViewModel[] = [];
                if (tzResponse.ok) {
                    tzs = await tzResponse.json() as TimeZoneViewModel[];
                    setAvailableTimeZones(tzs);
                } else {
                    console.error("Ошибка загрузки временных зон");
                    context.showMessage("Ошибка загрузки списка временных зон", true);
                }

                if (jobsResponse.ok) {
                    const jobSettings = await jobsResponse.json() as JobSettings[];
                    console.log(`Загружены задачи для ${editorId}:`, JSON.stringify(jobSettings, null, 2));
                    const sanitizedJobSettings = jobSettings.map(job => ({
                        ...job,
                        timeZoneId: job.timeZoneId || "", // Гарантируем, что timeZoneId - строка
                        params: job.params || [], // Гарантируем, что params - это массив
                    }));
                    setCron(sanitizedJobSettings);
                    console.log(`Cron state установлен для ${editorId}:`, JSON.stringify(sanitizedJobSettings, null, 2));
                } else {
                    console.error(`Ошибка загрузки recurring jobs для ${editorId}`);
                    context.showMessage("Ошибка загрузки настроек планировщика", true);
                }
            } catch (error) {
                console.error("Ошибка при загрузке данных для редактора:", error);
                context.showMessage(`Ошибка загрузки данных: ${error}`, true);
            } finally {
                context.setLoading(false);
            }
        })();
    }, [props.editor.Id])

    const saveCron = async () => {
        if (cron.some(c => !c.timeZoneId)) {
            context.showMessage("Для всех задач планировщика должна быть выбрана временная зона.", true);
            return false;
        }

        const response = await fetch(`/Integrations/SaveRecurringJobs/${props.editor.Id}`, {
            method: 'POST', body: JSON.stringify(cron),
            headers: { 'Content-Type': 'application/json' }
        });
        const { message, error } = await response.json() as { message?: string, error?: string };
        if (message) {
            await props.reload()
            if (message == 'OK') return true
            context.showMessage(message, false);
        }
        if (error) {
            context.showMessage(error, true);
        }
        return false
    }

    const update = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        context.setLoading(true);
        var isNewIntegrationSystem = false;
        try {
            // добавление всех новых интеграционных систем
            console.log('Выбранные интеграционные системы', integrationSystems)
            var systemIds = [];
            for (let i = 0; i < integrationSystems.length; i++) {
                const system = integrationSystems[i] as IntegrationSystem
                if ((system as any).customOption) {
                    var newSystemResult = await fetch(`/Systems/Save`, { method: 'POST', body: JSON.stringify({ Title: system.Title, Id: crypto.randomUUID() }), headers: { 'Content-Type': 'application/json' } });
                    const { message, error, edit } = await newSystemResult.json() as { message?: string, error?: string, edit?: IntegrationSystem };
                    if (error) throw error;
                    if (!edit) throw 'Ошибка сохранения интеграционной системы';
                    systemIds.push(edit.Id);
                    isNewIntegrationSystem = true;
                } else
                    systemIds.push(system.Id);
            }
            // сохранение
            const response = await fetch(`/Integrations/SaveInputInterface`, {
                method: 'POST', body: JSON.stringify({
                    ...props.editor,
                    SystemIds: systemIds
                } as InputSetup),
                headers: { 'Content-Type': 'application/json' }
            });
            const { message, error } = await response.json() as { message?: string, error?: string };
            if (message) {
                await props.reload()
                if (message == 'OK' && await saveCron())
                    props.setEditor(undefined)
                if (message != 'OK') context.showMessage(message, false);
            }
            if (error) {
                context.showMessage(error, true);
            }
        } catch (error) {
            context.showMessage(`Ошибка загрузки ${error ?? ''}`, true);
        } finally {
            context.setLoading(false);
            if (isNewIntegrationSystem) await context.reloadEnvironment()
        }
    }

    const addCron = () => {
        let defaultTimeZoneId = "";
        try {
            // 1. Пытаемся получить временную зону браузера
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            // 2. Проверяем, есть ли она в нашем списке доступных зон
            if (availableTimeZones.find(tz => tz.id === browserTimeZone)) {
                defaultTimeZoneId = browserTimeZone;
            }
        } catch (e) {
            console.warn("Не удалось определить временную зону браузера или найти ее в списке:", e);
        }

        // 3. Если зона браузера не найдена или не определена, используем запасные варианты
        if (!defaultTimeZoneId) {
            if (availableTimeZones.find(tz => tz.id === "UTC")) {
                defaultTimeZoneId = "UTC"; // Предпочитаем UTC
            } else if (availableTimeZones.length > 0) {
                defaultTimeZoneId = availableTimeZones[0].id; // Первая доступная
            }
            // Если availableTimeZones пуст, defaultTimeZoneId останется ""
            // В этом случае Form.Select покажет "Выберите зону..."
        }

        setCron([...cron, {
            cronExpression: '0 0 * * *', // или другое дефолтное выражение
            id: crypto.randomUUID(),
            timeZoneId: defaultTimeZoneId,
            params: [], // Инициализируем пустым массивом
        }]);
    };

    const deleteCron = (id: string) => {
        setCron(cron.filter(c => c.id !== id));
    };

    const updateCronExpression = (id: string, newExpression: string) => {
        setCron(cron.map(c => c.id === id ? { ...c, cronExpression: newExpression } : c));
    };
    const updateCronTimeZone = (id: string, newtimeZoneId: string) => {
        setCron(cron.map(c => c.id === id ? { ...c, timeZoneId: newtimeZoneId } : c));
    };
    // 2. Состояние для модального окна редактирования параметров
    const [showParamsModal, setShowParamsModal] = useState(false);
    const [editingJobId, setEditingJobId] = useState<string | null>(null);
    const [currentParams, setCurrentParams] = useState<JobParam[]>([]);

    // 3. Функции-обработчики для модального окна параметров
    const openParamsEditor = (jobId: string) => {
        const job = cron.find(c => c.id === jobId);
        if (job) {
            setEditingJobId(jobId);
            // Создаем глубокую копию, чтобы изменения не затрагивали основной стейт до сохранения
            setCurrentParams(JSON.parse(JSON.stringify(job.params)));
            setShowParamsModal(true);
        }
    };

    const closeParamsEditor = () => {
        setShowParamsModal(false);
        setEditingJobId(null);
        setCurrentParams([]);
    };

    const saveParams = () => {
        if (editingJobId) {
            setCron(cron.map(c =>
                c.id === editingJobId ? { ...c, params: currentParams } : c
            ));
        }
        closeParamsEditor();
    };

    const updateParam = (index: number, field: keyof JobParam, value: string) => {
        const newParams = [...currentParams];
        newParams[index] = { ...newParams[index], [field]: value };
        setCurrentParams(newParams);
    };

    const addParam = () => {
        setCurrentParams([...currentParams, { name: '', value: '' }]);
    };

    const deleteParam = (index: number) => {
        setCurrentParams(currentParams.filter((_, i) => i !== index));
    };


    return (<>
        <Modal show={props.editor != undefined} onHide={() => props.setEditor(undefined)} centered size="xl">
            <Modal.Header closeButton className="mt-2">
                <div>
                    <h1 className="mt-3">{props.editor.Name}
                        <span className="badge bg-secondary ms-2">{props.editor.Tag}</span>
                        <span className="badge bg-secondary ms-2">{props.editor.Id}</span>
                    </h1>
                    <h2 className="mb-4">Входной интерфейс</h2>
                </div>
            </Modal.Header>
            <Modal.Body className="overflow-y-auto">
                <Container>
                    <Form onSubmit={update}>

                        <Form.Group className="has-validation w-100">
                            <Form.Label>Наименование</Form.Label>
                            <Form.Control placeholder="Наименование" value={props.editor.Name} required onChange={(e) => props.setEditor(({ ...props.editor, Name: e.target.value }))} />
                        </Form.Group>
                        <Form.Group className="has-validation w-100 mt-4">
                            <Form.Label>Буквенно-числовой идентификатор</Form.Label>
                            <Form.Control placeholder="Буквенно-числовой идентификатор" value={props.editor.Tag} required onChange={(e) => props.setEditor(({ ...props.editor, Tag: e.target.value }))} />
                        </Form.Group>

                        <Form.Group className="w-100 mt-4">
                            <Form.Label>Тип входного интерфейса</Form.Label>
                            <Form.Select onChange={(e) => props.setEditor(({ ...props.editor, InputType: e.target.value as InputTypes }))}>
                                <option value={InputTypes.Request} selected={props.editor.InputType == InputTypes.Request}>Асинхронный запрос</option>
                                <option value={InputTypes.RequestWithResponse} selected={props.editor.InputType == InputTypes.RequestWithResponse}>Синхронный запрос с ответом</option>
                            </Form.Select>
                        </Form.Group>

                        {props.editor.InputType == InputTypes.RequestWithResponse && <Form.Group className="w-100 mt-4">
                            <Form.Label>Сколько ждать ответ (для запроса с ответом), сек</Form.Label>
                            <Form.Control placeholder="Сколько ждать ответ (для запроса с ответом), сек" min={0} max={65535} type="number" value={props.editor.ResponseTimeoutSeconds} required onChange={(e) => {
                                var p = parseInt(e.target.value)
                                if (p && !isNaN(p) && p >= 0 && p < 65535)
                                    props.setEditor(({ ...props.editor, ResponseTimeoutSeconds: p }))
                            }} />
                        </Form.Group>}

                        <Form.Group className="w-100 mt-4">
                            <Form.Label>Интеграционные системы</Form.Label>
                            <Typeahead placeholder="Интеграционные системы" className="has-validation"
                                allowNew
                                multiple
                                newSelectionPrefix="Добавить новую интеграционную систему: "
                                selected={integrationSystems}
                                options={context.environment.integrationSystems}
                                labelKey='Title'
                                onChange={(selected) => setIntegrationSystems(selected.map((tt: any) => tt as IntegrationSystem))}
                            />
                        </Form.Group>

                        <Form.Group className="w-100 mt-4">
                            <Form.Label>URL интеграции</Form.Label>
                            <Form.Control readOnly value={`${window.location.origin}/i/${props.editor.Id}`} />
                            {props.editor.Tag?.length > 0 && <Form.Control readOnly value={`${window.location.origin}/i/${props.editor.Tag}`} className="mt-2" />}
                        </Form.Group>

                        <h5 className="mt-5">Планировщик задач</h5>
                        <Form.Group className="w-100 mt-3">
                            {cron.map(c => (
                                <div key={c.id} className="mb-2 align-items-center d-flex justify-content-between gap-2"> {/* Align items vertically */}
                                    <Form.Control
                                        type="text"
                                        placeholder="Выражение CRON"
                                        value={c.cronExpression}
                                        onChange={e => updateCronExpression(c.id, e.target.value)}
                                        required
                                    />

                                    {availableTimeZones.length > 0 ? (
                                        <Form.Select
                                            aria-label="Временная зона"
                                            value={c.timeZoneId} // Должна быть строка, возможно пустая
                                            onChange={e => updateCronTimeZone(c.id, e.target.value)}
                                            required
                                        >
                                            <option value="" disabled={c.timeZoneId !== ""}>
                                                Выберите зону...
                                            </option>
                                            {availableTimeZones.map(tz => (
                                                <option key={tz.id} value={tz.id}>
                                                    {tz.displayName}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    ) : (
                                        <Form.Control placeholder="Загрузка зон..." disabled />
                                    )}

                                    {/* 4. Кнопка "Параметры" */}
                                    <Button variant="primary" onClick={() => openParamsEditor(c.id)} className="text-nowrap">
                                        Параметры <Badge bg="secondary">{c.params.length}</Badge>
                                    </Button>
                                    <Button variant="danger" onClick={() => {
                                        if (window.confirm('Вы уверены?')) deleteCron(c.id)
                                    }}>
                                        Удалить
                                    </Button>
                                    <Form.Text muted className="text-nowrap">
                                        {(() => {
                                            try {
                                                return <>{cronstrue.toString(c.cronExpression, { locale: "ru" })}</>
                                            } catch (e) {
                                                return <>{e}</>
                                            }
                                        })()}
                                    </Form.Text>
                                </div>
                            ))}
                            <Button variant="light" onClick={addCron}>Добавить автоматический вызов интеграции</Button>
                        </Form.Group>

                        <div className="d-flex mt-3" style={{ gap: '1rem' }}>
                            <Button variant="primary" type="submit">Сохранить</Button>
                            <Button variant="secondary-outline" onClick={() => props.setEditor(undefined)}>Отмена</Button>
                        </div>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
        
        {/* 5. Модальное окно для редактирования параметров */}
        <Modal show={showParamsModal} onHide={closeParamsEditor} centered>
            <Modal.Header closeButton>
                <Modal.Title>Редактор параметров</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {currentParams.map((param, index) => (
                    <InputGroup className="mb-2" key={index}>
                        <Form.Control
                            placeholder="Имя параметра"
                            aria-label="Имя параметра"
                            value={param.name}
                            onChange={e => updateParam(index, 'name', e.target.value)}
                        />
                        <Form.Control
                            placeholder="Значение"
                            aria-label="Значение"
                            value={param.value}
                            onChange={e => updateParam(index, 'value', e.target.value)}
                        />
                        <Button variant="outline-danger" onClick={() => deleteParam(index)}>
                            &times;
                        </Button>
                    </InputGroup>
                ))}
                <Button variant="light" className="mt-2" onClick={addParam}>
                    Добавить параметр
                </Button>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeParamsEditor}>
                    Отмена
                </Button>
                <Button variant="primary" onClick={saveParams}>
                    Сохранить параметры
                </Button>
            </Modal.Footer>
        </Modal>
    </>
    )
}